/**
 * Auth Signup-Success Controller (EPIC-002 / 2.4)
 *
 * Renders the "Vérifiez votre email" landing right after POST /auth/signup.
 * Reads the user's email from the query string (?email=…) and inlines it in
 * the explanatory paragraph. The "Renvoyer le lien" button POSTs to
 * /auth/resend-verification with the same email.
 *
 * The endpoint always answers 200 with the same uniform body (anti-enum), so
 * we surface a generic success toast on resolution and a network/retry
 * message only when the request itself fails. The button is disabled for
 * 30 s after a successful click to discourage spam.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";

const RESEND_COOLDOWN_MS = 30_000;

export default class AuthSignupSuccessController extends BaseController {
  static targets = [
    "email",
    "resend",
    "resendSuccess",
    "resendError",
    "resendErrorMessage",
  ];

  connect() {
    const params = new URLSearchParams(window.location.search);
    const email = (params.get("email") || "").trim();

    if (email && this.hasEmailTarget) {
      this.emailTarget.textContent = email;
    }
    this.email = email;
  }

  async resend() {
    if (!this.email) {
      this.showResendError(
        "Adresse email introuvable. Recommencez votre inscription.",
      );
      return;
    }

    this.hideResendBanners();
    this.setBusy(true);

    try {
      await api.resendVerification(this.email);
      this.showResendSuccess();
      // Keep the button disabled for a short cooldown so a user cannot
      // hammer the resend endpoint. Reset only if the controller is still
      // connected.
      setTimeout(() => {
        if (this.hasResendTarget) {
          this.resendTarget.disabled = false;
          this.resendTarget.setAttribute("aria-busy", "false");
        }
      }, RESEND_COOLDOWN_MS);
    } catch (err) {
      this.setBusy(false);
      this.handleResendError(err);
    }
  }

  // ---------------------------------------------------------------------------
  // View helpers
  // ---------------------------------------------------------------------------

  showResendSuccess() {
    if (this.hasResendSuccessTarget) {
      this.resendSuccessTarget.hidden = false;
      this.resendSuccessTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  showResendError(message) {
    if (this.hasResendErrorMessageTarget) {
      this.resendErrorMessageTarget.textContent = message;
    }
    if (this.hasResendErrorTarget) {
      this.resendErrorTarget.hidden = false;
      this.resendErrorTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  hideResendBanners() {
    if (this.hasResendSuccessTarget) this.resendSuccessTarget.hidden = true;
    if (this.hasResendErrorTarget) this.resendErrorTarget.hidden = true;
  }

  handleResendError(err) {
    const status = err instanceof APIError ? err.status : 0;
    if (status === 0 || status === 408 || status >= 500) {
      this.showResendError("Réseau injoignable. Réessayez dans un instant.");
      return;
    }
    this.showResendError("Une erreur est survenue. Réessayez.");
  }

  setBusy(busy) {
    if (!this.hasResendTarget) return;
    this.resendTarget.disabled = busy;
    this.resendTarget.setAttribute("aria-busy", busy ? "true" : "false");
  }
}
